import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ExamSessionInput,
  ExamSessionSummaryView,
  ExamSessionView,
  InvigilatorOption,
  MarkableAttendance,
  MarkingAttemptView,
  MarkingQueueRow,
  RosterCandidateOption,
} from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPost, apiPut, type ApiError } from '../../../lib/api-client';

/**
 * S18 — exam sessions (ASM-04, ASM-05) and marking (ASM-06). Realtime hints name
 * `exam-sessions` and `marking`, so an invigilator's attendance appears on the
 * recruiter's open session, and a grader's queue empties as they mark.
 */

export const EXAM_SESSIONS_KEY = ['exam-sessions'] as const;
export const MARKING_KEY = ['marking'] as const;

export function examSessionKey(id: string): readonly unknown[] {
  return [...EXAM_SESSIONS_KEY, 'detail', id];
}

export function markingAttemptKey(attemptId: string): readonly unknown[] {
  return [...MARKING_KEY, 'attempt', attemptId];
}

export function useExamSessions(filter: { readonly when: 'upcoming' | 'past'; readonly jobId?: string }): UseQueryResult<readonly ExamSessionSummaryView[], ApiError> {
  return useQuery({
    queryKey: [...EXAM_SESSIONS_KEY, 'list', filter],
    queryFn: () => apiGet<readonly ExamSessionSummaryView[]>('/exam-sessions', { query: { ...filter } }),
    placeholderData: (previous) => previous,
  });
}

export function useExamSession(id: string): UseQueryResult<ExamSessionView, ApiError> {
  return useQuery({
    queryKey: examSessionKey(id),
    queryFn: () => apiGet<ExamSessionView>(`/exam-sessions/${id}`),
  });
}

export function useSessionCandidates(id: string, isEnabled: boolean): UseQueryResult<readonly RosterCandidateOption[], ApiError> {
  return useQuery({
    queryKey: [...EXAM_SESSIONS_KEY, 'candidates', id],
    queryFn: () => apiGet<readonly RosterCandidateOption[]>(`/exam-sessions/${id}/candidates`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useInvigilators(jobId: string | null): UseQueryResult<readonly InvigilatorOption[], ApiError> {
  return useQuery({
    queryKey: [...EXAM_SESSIONS_KEY, 'invigilators', jobId],
    queryFn: () => apiGet<readonly InvigilatorOption[]>('/exam-sessions/invigilators', { query: { jobId: jobId ?? '' } }),
    enabled: jobId !== null,
    placeholderData: (previous) => previous,
  });
}

export type SessionStep = 'lock' | 'start' | 'finish' | 'complete';

export interface RosterAddResult {
  readonly added: readonly string[];
  readonly refused: readonly { readonly applicationId: string; readonly candidateName: string; readonly reason: string }[];
}

export const examSessionsApi = {
  create: (jobId: string, session: ExamSessionInput) => apiPost<ExamSessionView>('/exam-sessions', { jobId, session }),
  update: (id: string, version: number, session: ExamSessionInput) => apiPut<ExamSessionView>(`/exam-sessions/${id}`, { version, session }),
  step: (id: string, step: SessionStep, version: number) => apiPost<ExamSessionView>(`/exam-sessions/${id}/${step}`, { version }),
  reschedule: (id: string, version: number, startsAt: string, venue: string) => apiPost<ExamSessionView>(`/exam-sessions/${id}/reschedule`, { version, startsAt, venue }),
  cancel: (id: string, version: number, reason: string) => apiPost<ExamSessionView>(`/exam-sessions/${id}/cancel`, { version, reason }),
  add: (id: string, applicationIds: readonly string[]) => apiPost<RosterAddResult>(`/exam-sessions/${id}/roster`, { applicationIds }),
  remove: (id: string, attendanceId: string) => apiDelete<ExamSessionView>(`/exam-sessions/${id}/roster/${attendanceId}`),
  attendance: (id: string, marks: readonly { readonly attendanceId: string; readonly status: MarkableAttendance }[]) =>
    apiPost<ExamSessionView>(`/exam-sessions/${id}/attendance`, { marks }),
  results: (id: string, results: readonly { readonly attendanceId: string; readonly score: number }[]) => apiPost<ExamSessionView>(`/exam-sessions/${id}/results`, { results }),
  message: (id: string, attendanceId: string) => apiPost<{ readonly text: string }>(`/exam-sessions/${id}/roster/${attendanceId}/message`),
};

// --- Marking (ASM-06) ---------------------------------------------------------------------

export function useMarkingQueue(): UseQueryResult<readonly MarkingQueueRow[], ApiError> {
  return useQuery({
    queryKey: [...MARKING_KEY, 'queue'],
    queryFn: () => apiGet<readonly MarkingQueueRow[]>('/marking'),
    placeholderData: (previous) => previous,
  });
}

export function useMarkingAttempt(attemptId: string): UseQueryResult<MarkingAttemptView, ApiError> {
  return useQuery({
    queryKey: markingAttemptKey(attemptId),
    queryFn: () => apiGet<MarkingAttemptView>(`/marking/${attemptId}`),
  });
}

export function markAnswer(attemptId: string, questionId: string, points: number, note: string | null): Promise<MarkingAttemptView> {
  return apiPut<MarkingAttemptView>(`/marking/${attemptId}/answers/${questionId}`, { points, note });
}

// The Dhaka date-and-time helpers moved to `lib/dhaka-time.ts` in S19, which needs them too.
export { dhakaToIso, isoToDhaka } from '../../../lib/dhaka-time';
